import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { TransmissionService } from 'src/app/services/transmission/transmission.service';
import { TransmissionModalService } from 'src/app/services/transmission-modal/transmission-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-transmission',
  templateUrl: './transmission.component.html',
  styleUrls: ['./transmission.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class TransmissionComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  transmissions: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedTransmissions: string[] = [];

  constructor(
    private _transmissionService: TransmissionService,
    private _transmissionModalService: TransmissionModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadTransmissions();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadTransmissions(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedTransmissions = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._transmissionService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.transmissions = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.transmissions = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading transmissions:', error);
          this.transmissions = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadTransmissions(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadTransmissions(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadTransmissions();
  }

  openAddTransmissionModal(): void {
    this._transmissionModalService.clearSelectedTransmission();
    const modalElement = document.getElementById('kt_modal_transmission_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(transmission: any): void {
    this._transmissionModalService.setSelectedTransmission(transmission);
    const modalElement = document.getElementById('kt_modal_transmission_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteTransmission(transmission: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${transmission.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(transmission);
      }
    });
  }

  private performDelete(transmission: any): void {
    const transmissionId = transmission._id || transmission.id;
    this.subscriptions.push(
      this._transmissionService.DeleteDataById(transmissionId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Transmission has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadTransmissions(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete transmission.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting transmission.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onTransmissionAdded(): void {
    console.log('Transmission added, refreshing list...');
    this.loadTransmissions(this.currentPage, this.searchTerm, this.filters);
  }

  onTransmissionUpdated(): void {
    console.log('Transmission updated, refreshing list...');
    this.loadTransmissions(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(transmissionId: string): boolean {
    return this.selectedTransmissions.includes(transmissionId);
  }

  isAllSelected(): boolean {
    return this.transmissions.length > 0 && this.selectedTransmissions.length === this.transmissions.length;
  }

  isIndeterminate(): boolean {
    return this.selectedTransmissions.length > 0 && this.selectedTransmissions.length < this.transmissions.length;
  }

  toggleSelection(transmissionId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedTransmissions.includes(transmissionId)) {
        this.selectedTransmissions.push(transmissionId);
      }
    } else {
      this.selectedTransmissions = this.selectedTransmissions.filter(id => id !== transmissionId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedTransmissions = this.transmissions.map(transmission => transmission._id);
    } else {
      this.selectedTransmissions = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-transmission-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-transmission-table-toolbar="selected"]');

    if (this.selectedTransmissions.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedTransmissions(): void {
    if (this.selectedTransmissions.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedTransmissions.length} selected transmission(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedTransmissions.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._transmissionService.BulkDelete(this.selectedTransmissions).subscribe({
        next: () => {
          this.loadTransmissions();
          this.selectedTransmissions = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected transmissions have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting transmissions:', error);
          Swal.fire('Error!', 'Failed to delete selected transmissions.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
